import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, Play, Square, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const n = (v) => Number(v || 0).toLocaleString("en-GB");

/** Admin → Aircall AI: one-off historic summary backfill (start / progress / cancel). */
export default function AircallAiSettings() {
  const [status, setStatus] = useState(null);
  const [since, setSince] = useState("");
  const [busy, setBusy] = useState("");

  const load = async () => {
    try { setStatus((await api.get("/admin/aircall-ai-backfill/full")).data); } catch (e) { toast.error(e?.response?.data?.detail || "Could not load status"); }
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const start = async () => {
    setBusy("start");
    try {
      const r = (await api.post(`/admin/aircall-ai-backfill/full${since ? `?since=${since}` : ""}`)).data;
      r.started ? toast.success(r.message) : toast.warning(r.message);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start"); } finally { setBusy(""); }
  };
  const cancel = async () => {
    setBusy("cancel");
    try { await api.delete("/admin/aircall-ai-backfill/full"); toast.success("Cancelling — it stops after the current call"); setTimeout(load, 3000); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel"); } finally { setBusy(""); }
  };
  const tick = async () => {
    setBusy("tick");
    try { const r = (await api.post("/admin/aircall-ai-backfill")).data; toast.success(`Checked ${r.checked} recent calls — ${r.stored} summaries stored`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Poll failed"); } finally { setBusy(""); }
  };

  const job = status?.job;
  const running = !!status?.task_alive;
  const pct = job?.total ? Math.min(100, Math.round(((job.checked || 0) / job.total) * 100)) : 0;
  return (
    <Card data-testid="aircall-ai-settings">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-emerald-700" />Aircall AI call summaries</CardTitle>
        <CardDescription>Every answered call (UK + AU) gets its Aircall AI summary on the customer timeline automatically — a poll runs every 15 minutes. Use the one-off backfill below to fill in history from inception.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid sm:grid-cols-3 gap-3 text-sm">
          <div className="rounded-md border p-3"><div className="text-xs text-muted-foreground">Calls with an AI summary</div><div className="text-lg font-semibold" data-testid="aircall-ai-with-summary">{n(status?.calls_with_summary)}</div></div>
          <div className="rounded-md border p-3"><div className="text-xs text-muted-foreground">Answered calls still to check</div><div className="text-lg font-semibold" data-testid="aircall-ai-remaining">{n(status?.remaining)}</div></div>
          <div className="rounded-md border p-3"><div className="text-xs text-muted-foreground">Backfill</div>
            <div className="flex items-center gap-2"><Badge variant={running ? "default" : "outline"} data-testid="aircall-ai-job-status">{running ? "running" : job?.status || "idle"}</Badge>
              {job && <span className="text-xs text-muted-foreground">{n(job.checked)} / {n(job.total)} · {n(job.stored)} stored</span>}</div>
            {running && <div className="mt-2 h-2 rounded bg-slate-100 overflow-hidden"><div className="h-2 bg-emerald-600 transition-all" style={{ width: `${pct}%` }} /></div>}
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div><Label className="text-xs">Only calls since (optional)</Label><Input type="date" value={since} onChange={(e) => setSince(e.target.value)} className="h-9 w-44" disabled={running} data-testid="aircall-ai-since" /></div>
          {!running ? (
            <Button onClick={start} disabled={!!busy} data-testid="aircall-ai-start-btn">{busy === "start" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}Start historic backfill</Button>
          ) : (
            <Button variant="destructive" onClick={cancel} disabled={!!busy} data-testid="aircall-ai-cancel-btn"><Square className="w-4 h-4 mr-1" />Stop</Button>
          )}
          <Button variant="outline" onClick={tick} disabled={!!busy || running} data-testid="aircall-ai-tick-btn">{busy === "tick" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}Poll recent calls now</Button>
        </div>
        <p className="text-xs text-muted-foreground">Runs in the background at Aircall's rate limit (~50 calls/min, roughly 8 hours for the full history) and is resumable — if the server restarts just press Start again. Calls before Aircall AI was enabled, or taken by agents without an AI licence, are marked "no data" and never re-checked.</p>
      </CardContent>
    </Card>
  );
}
